// Capacitor builds load the SPA from `capacitor://localhost` (iOS) or
// `https://localhost` (Android, when androidScheme is "https"). Either way,
// relative `/api/*` calls resolve against the local origin and never reach
// the real server. This module rewrites those at the fetch/sendBeacon level
// once at boot. NextAuth and the 100+ direct `fetch('/api/...')` callers
// all flow through here without changes.

import { Capacitor } from '@capacitor/core';

const isCapacitorRuntime = (): boolean => {
  if (typeof window === 'undefined') return false;
  return Capacitor.isNativePlatform();
};

const stripTrailingSlash = (s: string): string => s.replace(/\/+$/, '');

export const getApiOrigin = (): string | null => {
  if (!isCapacitorRuntime()) return null;
  // Only rewrite for an actual Capacitor build (the bundle loads from
  // capacitor://localhost, so /api/* must be redirected to the real backend).
  const isCapacitorBuild =
    process.env.BUILD_FOR_CAPACITOR_STG === 'true' ||
    process.env.BUILD_FOR_CAPACITOR === 'true';
  if (!isCapacitorBuild) return null;
  // Use the per-environment API host baked in at build time:
  //   .env.dev → dev.sellwild.com   .env.stg → new.sellwild.com   .env.prod → sellwild.com
  // Previously this read NEXT_PUBLIC_STG_DOMAIN, which is hardcoded to
  // new.sellwild.com in EVERY env file — so `npm run ios:dev` (a .env.dev
  // build) was wrongly hitting staging instead of the local dev server, and
  // every listings fetch silently failed → empty grid / "nothing on cat change".
  const origin = process.env.NEXT_PUBLIC_API_URL;
  return origin ? stripTrailingSlash(origin) : null;
};

const rewriteApiUrl = (url: string, origin: string): string => {
  if (!url.startsWith('/api/')) return url;
  return `${origin}${url}`;
};

/** An absolute capacitor:// address of an API route, moved onto the live origin; null for anything else. */
const rewriteCapacitorUrl = (url: URL, origin: string): string | null =>
  url.protocol === 'capacitor:' && url.pathname.startsWith('/api/') ? `${origin}${url.pathname}${url.search}${url.hash}` : null;

const rewriteRequestUrl = (input: RequestInfo | URL, origin: string): RequestInfo | URL => {
  if (typeof input === 'string') return rewriteApiUrl(input, origin);
  if (input instanceof URL) return rewriteCapacitorUrl(input, origin) ?? input;
  // A Request. Its url is always absolute: a relative '/api/x' was resolved
  // against the app's own capacitor://localhost, so it is read like a URL.
  // (Matching it against '/api/' instead never matched, so a Request sent to
  // an API route went to the WebView's own origin and failed.)
  const rewritten = rewriteCapacitorUrl(new URL(input.url), origin);
  return rewritten === null ? input : new Request(rewritten, input);
};

let installed = false;

export function installCapacitorApiPatch(): void {
  if (installed) return;
  const origin = getApiOrigin();
  if (!origin) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const rewritten = rewriteRequestUrl(input, origin);
    // Cross-origin fetches under capacitor:// need credentials to forward
    // the NextAuth session cookie. Only force when the URL was rewritten
    // and the caller didn't already set a credentials mode.
    if (rewritten !== input && (!init || !init.credentials)) {
      init = { ...(init || {}), credentials: 'include' };
    }
    return originalFetch(rewritten as RequestInfo, init);
  };

  // Beacon API refuses non-HTTP(S) origins, and even when rewritten its
  // application/json bodies trigger a CORS preflight which the API routes
  // don't answer (405). Capacitor app has no real page-unload, so the
  // fire-and-forget property of beacon doesn't matter here. Route beacons
  // through fetch so they ride the CapacitorHttp native HTTP path —
  // bypasses CORS.
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon = (url: string | URL, data?: BodyInit | null): boolean => {
      const urlStr = typeof url === 'string' ? url : url.toString();
      const finalUrl = urlStr.startsWith('/api/') ? `${origin}${urlStr}` : urlStr;
      // keepalive is harmless under CapacitorHttp; with the native HTTP
      // transport, the request runs to completion regardless of WebView state.
      const send = (body: BodyInit | null, contentType?: string): void => {
        void window.fetch(finalUrl, {
          method: 'POST',
          body,
          credentials: 'include',
          keepalive: true,
          ...(contentType ? { headers: { 'Content-Type': contentType } } : {}),
        }).catch(() => { /* analytics beacons are best-effort */ });
      };
      // CapacitorHttp's native layer can't serialize a Blob/ArrayBuffer request
      // body — it throws `CapacitorUrlRequestError error 0` and the beacon never
      // sends (this silently killed every error-log/analytics beacon on iOS).
      // Read it to a string and forward with the Blob's own content type so the
      // API route's body parser still sees the right shape.
      if (data instanceof Blob) {
        void data.text().then((text) => send(text, data.type || 'application/json'));
      } else {
        send(data ?? null);
      }
      return true;
    };
  }
}
